import { Link, useRouterState } from "@tanstack/react-router";
import {
  ScrollArea,
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarInset,
  SidebarTrigger,
} from "@flint/ui";
import { hasPermission } from "@flint/api-contract";
import type { Permission } from "@flint/api-contract";
import { WorkspaceShell, BrandSquare } from "../workspace/index.ts";
import type { BreadcrumbItem } from "../workspace/components/breadcrumbs.tsx";
import { usePermissions } from "../../lib/permissions.ts";
import { STORE_NAV } from "./store-nav.ts";
import { StoreSidebarUser } from "./store-sidebar-user.tsx";
import { readSidebarCookie } from "./read-sidebar-cookie.ts";

/** A link is active on its own route and on anything nested under it. */
function isActive(pathname: string, to: string): boolean {
  if (to === "/store/admin") return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

export interface StoreAdminLayoutProps {
  breadcrumbs: BreadcrumbItem[];
  children: React.ReactNode;
  /**
   * Suppress the thin inset header row (which only holds the `SidebarTrigger`).
   * The appearance editor is a full-bleed canvas with its own toolbar, so it
   * hides this row and hosts the trigger itself. Every other page keeps it.
   */
  hideInsetHeader?: boolean;
  /**
   * Drop the content area's page padding and 1280px max-width so a page can
   * paint edge-to-edge (the appearance editor). Every other page stays padded
   * and centred.
   */
  fullBleed?: boolean;
}

/**
 * Shell for every `/store/admin/*` page — the module's collapsible left rail
 * plus the scrolling page content.
 *
 * Built on the `@flint/ui` Sidebar primitive, which supplies the 248px /
 * 56px widths, the `sidebar_state` cookie, Cmd/Ctrl+B, collapsed-state
 * tooltips and the sub-`lg` drawer. Grouping and order come from `STORE_NAV`,
 * mirroring the wireframe deck at `prototype/store/admin/all.html`.
 *
 * `hideTopbar` suppresses the workspace topbar — this rail carries the brand,
 * the nav and the account chip, so a topbar would be a second competing
 * navigation surface. `StoreSidebarUser` is therefore also the only way back
 * to the rest of Flint (its menu holds "Flint app").
 *
 * Two containment overrides are required because the primitive is written for
 * a page that owns the viewport, and here it's nested inside `WorkspaceShell`:
 * `min-h-0` replaces the provider's and inset's `min-h-svh`, and the rail's
 * fixed positioner becomes `absolute` against the `relative` provider. Without
 * them the rail escapes its pane and rides over the trial banner.
 */
export function StoreAdminLayout({
  breadcrumbs,
  children,
  hideInsetHeader,
  fullBleed,
}: StoreAdminLayoutProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const permissions = usePermissions();

  // Hide items the member can't reach, then drop any group left empty —
  // same contract as `visibleNavGroups()` for the workspace nav.
  const groups = STORE_NAV.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => !item.permission || hasPermission(permissions, item.permission as Permission),
    ),
  })).filter((group) => group.items.length > 0);

  return (
    <WorkspaceShell
      breadcrumbs={breadcrumbs}
      hideSectionTabs
      hideTopbar
      disableViewportScroll
      contentClassName="mx-0 flex h-full w-full max-w-none"
    >
      <SidebarProvider
        defaultOpen={readSidebarCookie()}
        className="relative h-full min-h-0 overflow-hidden"
      >
        <Sidebar collapsible="icon" className="absolute h-full">
          <SidebarHeader className="border-b border-sidebar-border">
            <div className="flex items-center gap-2.5 px-1 py-1">
              <BrandSquare />
              <span className="min-w-0 group-data-[collapsible=icon]:hidden">
                <span className="block truncate text-sm font-bold leading-tight text-ink">
                  Store admin
                </span>
                <span className="block truncate text-xs text-muted">Flint</span>
              </span>
            </div>
          </SidebarHeader>

          <SidebarContent>
            {groups.map((group) => (
              <SidebarGroup key={group.ariaLabel}>
                {group.label ? <SidebarGroupLabel>{group.label}</SidebarGroupLabel> : null}
                <SidebarMenu>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton
                          asChild
                          isActive={isActive(pathname, item.to)}
                          tooltip={item.label}
                        >
                          <Link to={item.to}>
                            <Icon aria-hidden />
                            <span>{item.label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroup>
            ))}
          </SidebarContent>

          <SidebarFooter className="border-t border-sidebar-border">
            <StoreSidebarUser />
          </SidebarFooter>
        </Sidebar>

        {/* `min-w-0`: the inset is a flex item, so its default `min-width: auto`
            lets wide content (the products table) push it past the viewport
            instead of shrinking — which breaks any `overflow-x-auto` inside it
            and clips the right of every page header. */}
        <SidebarInset className="min-h-0 min-w-0">
          {hideInsetHeader ? null : (
            <div className="flex shrink-0 items-center gap-2 border-b border-line bg-paper px-3 py-2">
              <SidebarTrigger />
            </div>
          )}
          {fullBleed ? (
            // Full-bleed pages (the appearance editor) own their internal
            // scrolling — the sections rail scrolls, the preview stays put. A
            // ScrollArea here would wrap them in an auto-height content box, so
            // the editor's `h-full` resolved to `auto` and collapsed to its
            // min-height, leaving dead space below on tall screens. Give it a
            // definite full height instead and let it manage its own overflow.
            <div className="min-h-0 flex-1 overflow-auto">{children}</div>
          ) : (
            <ScrollArea className="min-h-0 flex-1" viewportClassName="[&>div]:!block p-5 md:p-6">
              <div className="mx-auto w-full max-w-[1280px]">{children}</div>
            </ScrollArea>
          )}
        </SidebarInset>
      </SidebarProvider>
    </WorkspaceShell>
  );
}
